import { ParkingAreaService } from '../../../services/parking-area.service';
import { Component, ViewChild } from '@angular/core';
import { ParkingMapComponent } from '../parking-map/parking-map.component';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { AppConstants } from '../../../utils/AppConstants';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { ConfirmAlertComponent } from '../../shared/confirm-alert/confirm-alert.component';
import { ParkingArea } from '../../../models/ParkingArea';
import { Subscription } from 'rxjs';
import { AlertService } from '../../shared/alert/alert.service';

@Component({
  selector: 'app-parking-details',
  standalone: true,
  imports: [
    ParkingMapComponent,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    HasPermissionDirective,
    ConfirmAlertComponent,
  ],
  templateUrl: './parking-details.component.html',
  styleUrl: './parking-details.component.scss',
})
export class ParkingDetailsComponent {
  @ViewChild(ParkingMapComponent) parkingMapComponent!: ParkingMapComponent;

  paramSubscription!: Subscription;

  parkingForm: FormGroup;
  parkingArea: ParkingArea = {};

  detail_title: string = 'Nuova area';
  showConfirmationModal: boolean = false;
  confirmationMessage: string = '';


  constructor(
    private fb: FormBuilder,
    private parkingAreaService: ParkingAreaService,
    private routes: ActivatedRoute,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private alertService: AlertService,
    private fullSpinnerService: FullSpinnerService
  ) {
    fullSpinnerService.spinner(undefined);
    this.parkingForm = this.createForm();
  }

  ngOnInit(): void {
    this.paramSubscription = this.activatedRoute.params.subscribe(
      (parameter) => {
        let id = parameter['id'];
        let clone = parameter['clone'];
        if (id! > 0) {
          this.fullSpinnerService.spinner('');
          let parkingAreaPromise = clone && clone == 1 ? this.parkingAreaService.cloneById(id) : this.parkingAreaService.findById(id);
          parkingAreaPromise.subscribe({
            next: (value: ParkingArea) => {
              this.parkingArea = value;
              this.detail_title = this.parkingArea.name!;
              this.parkingArea.validityRange?.forEach(validityRange => {
                if (validityRange.startDay != null)
                  validityRange.validityRangeType = 'GENERIC';
                else validityRange.validityRangeType = 'FIXED';
              });

              if (this.parkingArea.validityRange && this.parkingArea.validityRange.length > 0) {
                let vrForm = this.parkingForm.get('validityRange') as FormArray;
                this.parkingArea.validityRange.forEach((vr, index) => {
                  this.addValidityRange(vrForm);
                  vr.timeRange?.forEach((tr) => {
                    this.addTimeRange(index, vrForm);
                    tr.daysArray = this.bitmaskToDayIndices(tr.days!).map(d => d.toString());
                  });
                });

              }

              this.parkingForm.patchValue(this.parkingArea);

              if (value.coordinatesJson && this.parkingMapComponent) {
                this.parkingMapComponent.setDrawnCoordinatesFromGeoJSON(
                  JSON.parse(value.coordinatesJson)
                );
              }
              this.fullSpinnerService.spinner(undefined);

            },
            error: () => {
              this.fullSpinnerService.spinner(undefined);
            },
          });
        }
      }
    );
  }

  bitmaskToDayIndices(mask: number): number[] {
    return [0, 1, 2, 3, 4, 5, 6].filter(index => (mask & (1 << index)) !== 0);
  }

  onSave() {
    const coordinatesJson = this.parkingForm.get('coordinatesJson')?.value;
    if ((Array.isArray(coordinatesJson) && coordinatesJson.length == 0) ||
      (!coordinatesJson || !coordinatesJson.features || coordinatesJson.features?.length == 0
      )) {
      this.parkingMapComponent.mapValidationError = true;
      this.alertService.addErrors("Mappa", ['Disegnare un\'area sulla mappa'], true)
      return;
    }

    this.parkingMapComponent.mapValidationError = false;

    if (this.parkingForm.valid) {
      if (!this.parkingForm.value.validityRange || this.parkingForm.value.validityRange.length == 0) {
        this.alertService.addErrors("Dati periodo", ['Inserire almento un periodo'], true)
        return;
      }
      else {
        for (const vr of this.parkingForm.value.validityRange) {
          if (!vr.timeRange || vr.timeRange.length == 0) {
            this.alertService.addErrors("Dati periodo", ['Inserire almento una fascia oraria'], true)
            return;
          }
        }
      }

      this.parkingArea = {
        ...this.parkingArea,                       
        ...this.parkingForm.value,              
        validityRange: this.parkingForm.value.validityRange?.map((vr: { timeRange: any[]; }, i: number) => ({
          ...this.parkingArea.validityRange?.[i],  
          ...vr,                                  
          timeRange: vr.timeRange?.map((tr: any, j:  number) => ({
            ...this.parkingArea.validityRange?.[i]?.timeRange?.[j],  
            ...tr                                                  
          })) ?? this.parkingArea.validityRange?.[i]?.timeRange     
        })) ?? this.parkingArea.validityRange                    
      };

      this.parkingArea.coordinatesJson = JSON.stringify(coordinatesJson);
      this.parkingArea.active = this.parkingForm.get('active')?.value;
      this.fullSpinnerService.spinner('');
      this.parkingArea.validityRange?.forEach(vr => {
        let mask;
        vr.timeRange?.forEach(tr => {
          tr.days = tr.daysArray?.reduce((mask, day) => mask | 1 << Number(day), 0);
        });
      });
      this.parkingAreaService.save(this.parkingArea).subscribe({
        next: (res) => {
          this.alertService.addErrors(
            AppConstants.titleSuccessMessage,
            [AppConstants.successMessage],
            true
          );
          this.goBack();
        },
        complete: () => {
          this.fullSpinnerService.spinner(undefined);
        },
        error: () => {
          this.alertService.addErrors(
            AppConstants.titleErrorMessage,
            [AppConstants.errorMessage],
            true
          );
          this.fullSpinnerService.spinner(undefined);
        },
      });
    }
  }

  delete() {
    this.fullSpinnerService.spinner('');
    this.parkingAreaService.delete(this.parkingArea.id!).subscribe({
      next: (value) => {
        this.alertService.addErrors(
          AppConstants.titleSuccessMessage,
          [AppConstants.successMessage],
          true
        );
      },
      complete: () => {
        this.fullSpinnerService.spinner(undefined);
        this.goBack();
      },
      error: (err) => {
        this.alertService.addErrors(
          AppConstants.titleErrorMessage,
          [AppConstants.errorMessage],
          true
        );
      },
    });
  }

  removeValidityRange(index: number) {
    this.validityRange.removeAt(index);
  }

  removeTimeRange(validityIndex: number, timeIndex: number) {
    const timeRange = this.validityRange
      .at(validityIndex)
      .get('timeRange') as FormArray;
    timeRange.removeAt(timeIndex);
  }

  confirmDelete() {
    this.showConfirmationModal = true;
    this.confirmationMessage = 'Sicuro di voler eliminare l\'area di parcheggio ?';
  }

  cancelDelete() {
    this.showConfirmationModal = false;
  }

  onCoordinatesChange(geojson: any) {
    this.parkingForm.get('coordinatesJson')?.setValue(geojson);
  }

  goBack() {
    this.router.navigate(['parking']);
  }

  private createForm() {
    let form = this.fb.group({
      code: ['', [Validators.required]],
      name: ['', [Validators.required]],
      active: [false],
      description: ['', Validators.required],
      coordinatesJson: [[], Validators.required],
      validityRange: this.fb.array([])
    });
    return form;
  }

  get validityRange() {
    return this.parkingForm.get('validityRange') as FormArray;
  }
  addValidityRange(newValidityRange?: FormArray<any>) {
    let vRage = newValidityRange ?? this.validityRange;
    vRage.push(
      this.fb.group({

        validityRangeType: ['GENERIC', Validators.required],
        startDate: [undefined, Validators.required],
        endDate: [undefined, Validators.required],
        startDay: [undefined, Validators.required],
        startMonth: [undefined, Validators.required],
        endDay: [undefined, Validators.required],
        endMonth: [undefined, Validators.required],
        timeRange: this.fb.array([]),
      }, { validators: this.conditionalRequired() })
    );
  }


  addTimeRange(validityIndex: number, newValidityRange?: FormArray<any>) {
    let vRage = newValidityRange ?? this.validityRange;
    const timeRange = vRage
      .at(validityIndex)
      .get('timeRange') as FormArray;
    timeRange.push(
      this.fb.group({
        rate: ['', Validators.required],
        startTime: ['', Validators.required],
        endTime: ['', Validators.required],
        rateHH: ['', Validators.required],
        limitHH: ['', Validators.required],

        daysArray: [['0', '1', '2', '3', '4', '5', '6'], Validators.required],
      }),
    );
  }

  getTimeRange(validityIndex: number): FormArray {
    return this.validityRange.at(validityIndex).get('timeRange') as FormArray;
  }


  conditionalRequired(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const validityRangeType = group.get('validityRangeType')?.value;

      if (validityRangeType === 'FIXED') {
        group.get('startDate')?.setValidators(Validators.required);
        group.get('endDate')?.setValidators(Validators.required);
        group.get('startDay')?.setErrors(null);
        group.get('startMonth')?.setErrors(null);
        group.get('endDay')?.setErrors(null);
        group.get('endMonth')?.setErrors(null);

        return {};
      }
      else if (validityRangeType === 'GENERIC') {
        group.get('startDate')?.setErrors(null);
        group.get('endDate')?.setErrors(null);
        group.get('startDay')?.setValidators(Validators.required);
        group.get('startMonth')?.setValidators(Validators.required);
        group.get('endDay')?.setValidators(Validators.required);
        group.get('endMonth')?.setValidators(Validators.required);

        return {};
      }



      return null;
    };
  }

  ngOnDestroy(): void {
    this.paramSubscription.unsubscribe();
  }
}
